import React, { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import api from './api';
import { CardLoader } from './Loader';

const CommentsSection = ({ comments, setComments, articleId, commentsLoading }) => {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentForm, setCommentForm] = useState({
    visitor_name: '',
    visitor_email: '',
    content: ''
  });
  const [submittingComment, setSubmittingComment] = useState(false);

  // Fonction pour récupérer les commentaires
  const fetchComments = async () => {
    try {
      const response = await api.get(`/api/articles/${articleId}/comments/`);
      setComments(response.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des commentaires:', err);
      setComments([]);
    }
  };

  // Fonction pour soumettre un commentaire
  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.visitor_name || !commentForm.visitor_email || !commentForm.content) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      setSubmittingComment(true);
      await api.post('/api/comments/', {
        ...commentForm,
        article: articleId
      });
      
      // Réinitialiser le formulaire
      setCommentForm({
        visitor_name: '',
        visitor_email: '',
        content: ''
      });
      setShowCommentForm(false);
      
      // Recharger les commentaires
      await fetchComments();
      
      alert('Votre commentaire a été publié avec succès !');
    } catch (err) {
      console.error('Erreur lors de l\'envoi du commentaire:', err);
      alert('Erreur lors de l\'envoi du commentaire');
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-2xl font-bold text-gray-800">
            Commentaires ({comments.length})
          </h3>
          <button
            onClick={() => setShowCommentForm(!showCommentForm)}
            className="flex items-center text-[#089556] hover:text-[#07D1EB] transition-colors duration-200"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Ajouter un commentaire
          </button>
        </div>
        
        {/* Comment Form */}
        {showCommentForm && (
          <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">
              Ajouter un commentaire
            </h4>
            
            <form onSubmit={submitComment}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={commentForm.visitor_name}
                    onChange={(e) => setCommentForm({...commentForm, visitor_name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={commentForm.visitor_email}
                    onChange={(e) => setCommentForm({...commentForm, visitor_email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire *
                </label>
                <textarea
                  value={commentForm.content}
                  onChange={(e) => setCommentForm({...commentForm, content: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#089556] focus:border-transparent"
                  placeholder="Partagez votre avis sur cet article..."
                  required
                />
              </div>
              
              <div className="flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCommentForm(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="px-6 py-2 bg-[#089556] hover:bg-[#07D1EB] text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  {submittingComment ? 'Envoi...' : 'Publier le commentaire'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Comments List */}
        {commentsLoading ? (
          <div className="text-center py-8">
            <CardLoader />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <div className="h-12 w-12 text-gray-400 mx-auto mb-4">
              <MessageCircle className="w-full h-full" />
            </div>
            <p className="text-gray-600">Aucun commentaire pour le moment.</p>
            <p className="text-gray-500 text-sm">Soyez le premier à commenter cet article !</p>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800">{comment.visitor_name}</h5>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-justify">{comment.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CommentsSection;